import * as i18next from 'i18next'
import * as Immutable from 'immutable'
import { List } from 'immutable'
import {
  async,
  AsyncState,
  fromJSX,
  HttpResult,
  IOWidget,
  Link,
  Option,
  some,
  Updater,
  Widget
} from 'widgets-for-react'
import { Pages, PublicSiteState } from '../../../../../root/state'
import { CookiebarState } from '../../cms/blocks/cookiebar/cookiebar.types.mod'
import { are_cookies_different } from '../../cms/blocks/cookiebar/cookiebar.widget.mod'
import { Page, TemplateState } from '../../cms/page/types.gen'
import { PageTemplateComponent_AUX } from '../../cms/page/widget.gen'
import { autoComplete, search } from '../../drupal/api/api.mod'
import { parseImageUrl } from '../../drupal/rendering/image_utils.mod'
import { PaginatedItem, SearchResultItem } from '../../drupal/types_and_parser/extra_types.mod'
import { ParsedTaxonomyTermRelatedContent } from '../../drupal/types_and_parser/types.ext.gen'
import { page } from '../templates/drupal_page.mod'
import { loaderFull } from '../templates/loader.mod'
import { Pagination } from '../templates/pagination.mod'
import { getSearchParams } from '../utils/helpers'
import { defaultLanguage } from '../utils/history.mod'
import { AbstractParsedUpdate } from './shared/AbstractParsedUpdate'
import { mkPagination } from './shared/SimpelPagination'
import React = require('react')
import { useRef } from 'react'

export type UpdateOverviewState = {
  kind: UpdateOverviewKind
  overview_page: number
  get_updates: AsyncState<PaginatedItem<AbstractParsedUpdate>>
  get_searches: AsyncState<PaginatedItem<SearchResultItem>>
  get_tags: AsyncState<PaginatedItem<ParsedTaxonomyTermRelatedContent>>
  sustainability_tags: AsyncState<SustainabilityTermResponse[]>
  current_tags: Immutable.List<number>
  updates_jsx: Option<JSX.Element>
  current_query: string
  get_suggestions: AsyncState<Immutable.List<string>>
  state: PageTemplateState
}

export type UpdateOverviewKind = 'updates' | 'project-updates' | 'sustainability-updates'

export type PageTemplateState = {
  cookiestate: CookiebarState
  page: TemplateState & {
    display: (placeholder: Widget<Updater<TemplateState>>) => (state: PublicSiteState) => Widget<Updater<TemplateState>>
    pageDisplay: (
      public_state: PublicSiteState,
      set_public_site_state: (output: Updater<PublicSiteState>) => void
    ) => IOWidget<TemplateState, Updater<TemplateState>>
    get_page: (slug: string) => AsyncState<Page>
  }
}

const zeroPager = <T extends any>() => ({
  items: Array<T>(),
  pager: {
    current_page: 0,
    items_per_page: 10,
    total_items: 0,
    total_pages: 0
  }
})

export interface SustainabilityTermResponse {
  id: number
  name: string
}

export const getSustainabilityTerms = (): Promise<HttpResult<SustainabilityTermResponse[]>> =>
  fetch(`/api/drupal/en/api/sustainability/child-terms`)
    .then((response) => {
      if (response.ok) {
        return response.json()
      }
      return Promise.reject(response.status)
    })
    .then(
      (data: SustainabilityTermResponse[]) =>
        ({ kind: 'result', value: data.map((x) => ({ ...x, id: Number(x.id) })), status: 200 } as HttpResult<
          SustainabilityTermResponse[]
        >)
    )
    .catch((reason) => {
      return { status: reason, kind: 'failed' }
    })

export const getTaxonomies = (pageKind: UpdateOverviewState['kind']) => {
  switch (pageKind) {
    case 'project-updates':
      return [1, 2, 3, 4, 5, 6]
    case 'sustainability-updates':
    // return [9]
    default:
      let params = getSearchParams()
      let taxonomiesRaw = params.get(taxonomies_key) || ''
      let splitted = taxonomiesRaw.split(',')
      let unfiltered_taxonomies = splitted.map((x) => +x)
      return unfiltered_taxonomies.filter((t) => t != 0)
  }
}

const taxonomies_key = 'filters'

export const update_overview = (_r: {
  routes: Pages
  state: UpdateOverviewState
  public_state: PublicSiteState
  set_public_state: (_: Updater<PublicSiteState>) => void
}): Widget<Updater<UpdateOverviewState>> => {
  const pageContentRef = useRef<HTMLDivElement>(null)
  const updatesOverviewRef = useRef<HTMLDivElement>(null)
  
  return fromJSX((setState) => {
    let r: [Pages, UpdateOverviewState] = [_r.routes, _r.state]
    let ss = r[1]
    let pageName = r[0].kind == 'update_overview' ? ss.kind : 'updates'
    let lang: string = r[0].kind == 'update_overview' ? r[0].lang : defaultLanguage

    let updateUrl = (lang: string, pageName: string, page: number, taxonomies: Immutable.List<number>) => {
      let taxonomy_part = taxonomies.size > 0 ? `?${taxonomies_key}=${taxonomies.join(',')}` : ''

      window.history.pushState({}, '', `/${lang}/${pageName}/${page}/${taxonomy_part}`)
    }
    let setPage = (page: number) => {
      if (page == undefined) return
      updateUrl(lang, pageName, page, ss.current_tags)
      pageContentRef.current?.scrollIntoView()
      setState((s) => ({ ...s, overview_page: page }))
    }
    let setTaxonomy = (taxonomy: number) => {
      let page: number = r[0].kind == 'update_overview' ? r[0].page || 0 : 0
      setState((s) => {
        let currentIndex = s.current_tags.findIndex((x) => x == taxonomy)
        let new_tags = currentIndex > -1 ? s.current_tags.delete(currentIndex) : s.current_tags.push(taxonomy)

        updateUrl(lang, pageName, page, new_tags)
        return { ...s, current_tags: new_tags, overview_page: 0 }
      })
    }

    let resetTaxonomy = () => {
      let page: number = r[0].kind == 'update_overview' ? r[0].page || 0 : 0
      updateUrl(lang, pageName, page, List())
      setState((s) => ({ ...s, current_tags: List(), overview_page: 0 }))
    }

    let setQuery = (query: string) =>
      setState((s) => ({ ...s, current_query: query, get_suggestions: autoComplete(query, lang), overview_page: 0 }))

    let submitSearch = (tags: List<number>) => {
      updateUrl(lang, pageName, 0, tags)
      setState((s) => ({ ...s, overview_page: 0, get_searches: search(ss.current_query, '', 0, 10000, lang) }))
      setTimeout(() => updatesOverviewRef.current?.scrollIntoView({ behavior: "smooth" }), 100)
    }

    if (ss.state.page.page.kind == 'loaded') {
      let updatesList =
        ss.get_updates.kind == 'loaded'
          ? ss.get_updates.value
          : ss.get_updates.kind == 'failed'
          ? zeroPager<AbstractParsedUpdate>()
          : null
      if (pageName == 'sustainability-updates' && updatesList != null) {
        updatesList = {
          ...updatesList,
          items: updatesList.items.filter((item) => item.field_category.includes('Sustainability'))
        }
      }

      let tagsList =
        ss.get_tags.kind == 'loaded'
          ? ss.get_tags.value
          : ss.get_updates.kind == 'failed'
          ? zeroPager<ParsedTaxonomyTermRelatedContent>()
          : null

      let search_results = ss.get_searches.kind == 'loaded' ? ss.get_searches.value : null

      ss = {
        ...ss,
        state: {
          ...ss.state,
          page: {
            ...ss.state.page,
            display: page(
              some(
                update_overview_block(
                  ss.current_tags.toArray(),
                  updatesList,
                  search_results,
                  ss.current_query.toLocaleLowerCase(), // To make matching easier
                  tagsList,
                  ss.sustainability_tags,
                  (page) => setPage(page),
                  (taxonomy) => setTaxonomy(taxonomy),
                  resetTaxonomy,
                  (query) => setQuery(query),
                  submitSearch,
                  ss.overview_page,
                  ss.get_updates.kind,
                  ss.kind,
                  pageContentRef,
                  updatesOverviewRef
                )
              ),
              pageContentRef
            )
          }
        }
      }
    }

    r[1] = ss

    let global_state: PublicSiteState = {
      ..._r.public_state,
      page: r[1].state.page
    }

    let update_overview_state = r[1]

    return (
      <>
        {async(ss.get_searches).run((a) => setState((s) => ({ ...s, get_searches: a(s.get_searches) })))}
        {async(ss.get_suggestions).run((a) => setState((s) => ({ ...s, get_suggestions: a(s.get_suggestions) })))}

        {r[1].state.page.page.kind == 'loaded'
          ? async(r[1].state.page.page.value.extra_data.breadcrumb)
              .map((a) => {
                setState((s1) => {
                  if (s1.state.page.page.kind == 'loaded') {
                    let a1 = a(s1.state.page.page.value.extra_data.breadcrumb)
                    s1 = {
                      ...s1,
                      state: {
                        ...s1.state,
                        page: {
                          ...s1.state.page,
                          display: page(s1.updates_jsx),
                          page: {
                            ...s1.state.page.page,
                            value: {
                              ...s1.state.page.page.value,
                              extra_data: { ...s1.state.page.page.value.extra_data, breadcrumb: a1 }
                            }
                          }
                        }
                      }
                    }
                    return s1
                  }
                })
              })
              .run((_) => {})
          : null}

        {async(update_overview_state.get_updates).run((res) =>
          setState((s) => {
            let new_async_state = res(s.get_updates)
            return { ...s, get_updates: new_async_state }
          })
        )}
        {async(update_overview_state.get_tags).run((res) =>
          setState((s) => {
            let new_async_state = res(s.get_tags)
            return { ...s, get_tags: new_async_state }
          })
        )}
        {async(update_overview_state.sustainability_tags).run((res) =>
          setState((s) => {
            let new_async_state = res(s.sustainability_tags)
            return { ...s, sustainability_tags: new_async_state }
          })
        )}

        {PageTemplateComponent_AUX(
          'update_overview',
          global_state.page.pageDisplay(global_state, _r.set_public_state),
          global_state.page.display,
          global_state
        )({
          blocks: global_state.page.blocks,
          images: global_state.page.images,
          page: global_state.page.page
        })
          .map<Updater<PublicSiteState>>((a) => (s3): PublicSiteState => {
            return { ...s3, page: { ...s3.page, ...a(s3.page) } }
          })
          .run((a) => {
            setState((res1) => {
              let state1 = a({ ...global_state, page: res1.state.page })
              if (are_cookies_different(state1.cookiestate, r[1].state.cookiestate)) {
                r[1].state.cookiestate = state1.cookiestate
              }
              return { ...res1, state: state1 }
            })
          })}
      </>
    )
  })}

let update_overview_block = (
  current_tags: number[],
  updates: PaginatedItem<AbstractParsedUpdate>,
  searches: PaginatedItem<SearchResultItem>,
  current_query: string,
  tags: PaginatedItem<ParsedTaxonomyTermRelatedContent>,
  sustainability_tags: AsyncState<SustainabilityTermResponse[]>,
  setPage: (page: number) => void,
  setTaxonomy: (taxonomy: number) => void,
  resetTaxonomy: () => void,
  setQuery: (query: string) => void,
  submitSearch: (tags: List<number>) => void,
  current_page: number,
  kind: 'loaded' | 'loading' | 'unloaded' | 'connection-error' | 'unauthorized' | 'failed',
  slug: UpdateOverviewState['kind'],
  pageContentRef: React.RefObject<HTMLDivElement>,
  updatesOverviewRef: React.RefObject<HTMLDivElement>
) => {
  let current_filters = [] // tags as text

  if (tags && tags.items && slug == 'updates') {
    current_filters = tags.items.filter((t) => current_tags.includes(t.id)).map((t) => t.label)
  } else if (slug == 'sustainability-updates' && sustainability_tags.kind == 'loaded') {
    current_filters = sustainability_tags.value.filter((t) => current_tags.includes(t.id)).map((t) => t.name)
  }

  let page = mkPagination(
    kind == 'loaded' && updates && updates.items && updates.items[0]
      ? updates.items.filter(
          (u) =>
            (current_filters.length == 0 ||
              current_filters.reduce((xs, x) => xs || u.field_category.includes(x), false)) &&
            (searches == null || searches.items.reduce<boolean>((xs, x) => xs || x.title == u.title, false))
          // && (current_query == "" || u.title.toLocaleLowerCase().includes(current_query))
        )
      : [],
    current_page,
    6
  )
  return (
    <>
      {slug == 'updates' ? (
        <section className="filters ls-container">
          <div className="filters__inner">
            <header className="filters__header">
              <h2 className="filters__title title--s">{i18next.t('Update:Filter:Title')}</h2>
            </header>
            <div className="filters__content">
              <ul id="tagUL01" className="filters__list tags">
                <>
                  {tags?.items?.map((tag, index) => (
                    <li
                      className={current_tags.includes(tag.id) ? 'tags__item tags__item--active' : 'tags__item'}
                      id={tag.id.toString()}
                      key={'tagitems_' + index}
                    >
                      <a className="tag tag--m" onClick={() => setTaxonomy(tag.id)}>
                        {tag.label}
                      </a>
                    </li>
                  ))}
                  <li className="tags__item tags__item--reset" aria-label="reset button">
                    <button disabled={current_tags.length == 0} className="tag tag--m" onClick={resetTaxonomy}>
                      {i18next.t('Update:Filter:Reset')}
                    </button>
                  </li>
                </>
              </ul>
            </div>
          </div>
        </section>
      ) : null}

      {slug == 'sustainability-updates' && sustainability_tags.kind == 'loaded' && (
        <section className="filters ls-container">
          <div className="filters__inner">
            <header className="filters__header">
              <h2 className="filters__title title--s">{i18next.t('Update:Filter:Title')}</h2>
            </header>
            <div className="filters__content">
              <ul id="tagUL01" className="filters__list tags">
                {sustainability_tags.value.map((tag, index) => (
                  <li
                    className={current_tags.includes(tag.id) ? 'tags__item tags__item--active' : 'tags__item'}
                    id={tag.id.toString()}
                    key={'tagitems_' + index}
                  >
                    <a className="tag tag--m" onClick={() => setTaxonomy(tag.id)}>
                      {tag.name}
                    </a>
                  </li>
                ))}
                <li className="tags__item tags__item--reset" aria-label="reset button">
                  <button disabled={current_tags.length == 0} className="tag tag--m" onClick={resetTaxonomy}>
                    {i18next.t('Update:Filter:Reset')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}

      <section className="block block--updates-overview" ref={updatesOverviewRef}>
        <div className="block__inner ls-container">
          <div className="block__content">
            <ul className="block__list grid">
              {kind == 'loading' ? (
                loaderFull.run((_) => {})
              ) : kind == 'loaded' && updates && updates.items && updates.items.length > 0 ? (
                <>
                  {page.items.map((update, index) => (
                    <li key={'grid_item_00' + index} className="grid__item">
                      <article className="grid__article">
                        <div className="grid__content">
                          <header className="grid__header">
                            <h3 className="grid__title title--s">{update?.title}</h3>
                            <Link to={update?.view_node} className="grid__link"></Link>
                          </header>
                          <time className="grid__date text--s" dateTime={update?.created}>
                            {update?.created}
                          </time>
                        </div>
                        <div className="grid__wrap">
                          <div className="grid__tags tags">
                            {update?.field_category?.split(', ')?.map((x, i) => (
                              <span key={'grid_tag00' + i} className="grid__tag tag tag--m">
                                {x}
                              </span>
                            ))}
                          </div>
                          <div className="grid__visual fit-object">
                            {update.field_media_image && (
                              <img
                                loading="lazy"
                                className="grid__image"
                                src={parseImageUrl(update.field_media_image)}
                                alt={update.title}
                              />
                            )}
                          </div>
                        </div>
                      </article>
                    </li>
                  ))}
                </>
              ) : (
                <div style={{ marginRight: 'auto', marginLeft: 'auto' }}>No Results</div>
              )}
            </ul>
          </div>
        </div>
      </section>

      {page && updates && updates.items && (
        <Pagination
          pager={{
            current_page: page.current_page,
            items_per_page: page.page_size,
            total_items: updates.items.length,
            total_pages: page.total_pages
          }}
          setPage={(page: number) => setPage(page)}
        />
      )}

      <section className="block block--search">
        <div className="block__inner ls-container">
          <form
            className="block__form"
            role="search"
            onSubmit={(e) => {
              e.preventDefault()
              submitSearch(List(current_tags))
            }}
          >
            <label className="block__label ">
              Keyword<span className="sr-text">search</span>
            </label>
            <div className="block__wrap">
              <input
                className="block__input"
                type="search"
                id="a11y-search"
                name="a11y-search"
                onChange={(e) => setQuery(e.target.value)}
              />
              <button className="block__button" type="submit">
                Find updates
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  )
}